import { CurveDataType, HorizontalAlignmentType, } from "../core/alignment/horizontal-alignment/interface";
import { SIMA } from "./sima";
import { SIMAElementClassifier, SIMARouteType, SIMA as SIMADoc, } from "@earthbrain/sima";
const getIPRoute = (ipLinePoints, surveyPointsMap) => {
    const ipRouteAttribute = {
        classifier: SIMAElementClassifier.ROUTE_BEGIN,
        route_number: 1,
        group_number: 1,
        route_name: "IP Line",
        route_type: SIMARouteType.IP_LINE,
    };
    const ipRouteElements = [];
    for (const ipPoint of ipLinePoints) {
        const surveyPoint = surveyPointsMap.get(ipPoint.id);
        if (surveyPoint) {
            ipRouteElements.push({
                classifier: SIMAElementClassifier.ROUTE_COORD,
                point_number: surveyPoint.pointNumber,
                point_name: surveyPoint.pointName,
            });
        }
    }
    const ipRoute = {
        routeAttributes: [ipRouteAttribute],
        routeElements: ipRouteElements,
    };
    return ipRoute;
};
const getSignedRadius = (circularSegment) => {
    const p1 = circularSegment.startPoint.localPoint;
    const pAnchor = circularSegment.anchorPoint.localPoint;
    const p2 = circularSegment.endPoint.localPoint;
    const v1x = pAnchor.x - p1.x;
    const v1y = pAnchor.y - p1.y;
    const v2x = p2.x - pAnchor.x;
    const v2y = p2.y - pAnchor.y;
    // 2D cross product of incoming and outgoing tangents: v1 x v2
    // Negative indicates a clockwise / right turn
    const cross = v1x * v2y - v1y * v2x;
    const isRightTurn = cross < 0;
    return isRightTurn
        ? Math.abs(circularSegment.element.radius)
        : -Math.abs(circularSegment.element.radius);
};
const getMainLineRoute = (alignment, surveyPointsMap) => {
    const mainLineRouteAttribute = {
        classifier: SIMAElementClassifier.ROUTE_BEGIN,
        route_number: 2,
        group_number: 1,
        route_name: "Main Line",
        route_type: SIMARouteType.MAIN_LINE,
    };
    const mainLineRouteElements = [];
    for (const segment of alignment.horizontalAlignment.segments) {
        switch (segment.type) {
            case CurveDataType.LINEAR:
                {
                    if (!segment.previous ||
                        segment.previous.type !== CurveDataType.LINEAR) {
                        const startSp = surveyPointsMap.get(segment.startPoint.id);
                        if (startSp) {
                            mainLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: startSp.pointNumber,
                                point_name: startSp.pointName,
                            });
                        }
                    }
                    const endSp = surveyPointsMap.get(segment.endPoint.id);
                    if (endSp) {
                        mainLineRouteElements.push({
                            classifier: SIMAElementClassifier.ROUTE_COORD,
                            point_number: endSp.pointNumber,
                            point_name: endSp.pointName,
                        });
                    }
                }
                break;
            case CurveDataType.CIRCULAR_SEGMENT:
                {
                    if (!segment.previous ||
                        segment.previous.type == CurveDataType.CIRCULAR_SEGMENT) {
                        const startSp = surveyPointsMap.get(segment.startPoint.id);
                        if (startSp) {
                            mainLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: startSp.pointNumber,
                                point_name: startSp.pointName,
                            });
                        }
                    }
                    const circularSegment = segment;
                    mainLineRouteElements.push({
                        classifier: SIMAElementClassifier.SIMPLE_CURVE,
                        direction: circularSegment.element.isBigArc ? 1 : 0,
                        radius: getSignedRadius(circularSegment),
                    });
                    if (!segment.next) {
                        const endSp = surveyPointsMap.get(segment.endPoint.id);
                        if (endSp) {
                            mainLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: endSp.pointNumber,
                                point_name: endSp.pointName,
                            });
                        }
                    }
                }
                break;
        }
    }
    const mainLineRoute = {
        routeAttributes: [
            mainLineRouteAttribute,
            {
                classifier: SIMAElementClassifier.ROUTE_ATTRIBUTES,
                additional_distance: 0,
            },
        ],
        routeElements: mainLineRouteElements,
    };
    return mainLineRoute;
};
const getCentralLineRoute = (alignment, surveyPointsMap) => {
    const centralLineRouteAttribute = {
        classifier: SIMAElementClassifier.ROUTE_BEGIN,
        route_number: 3,
        group_number: 1,
        route_name: "Central Line",
        route_type: SIMARouteType.CENTRAL_LINE,
    };
    const centralLineRouteElements = [];
    const crossSectionPoints = alignment
        .pointsToCreateCrossSection;
    for (const segment of alignment.horizontalAlignment.segments) {
        switch (segment.type) {
            case CurveDataType.LINEAR:
                {
                    if (!segment.previous ||
                        segment.previous.type !== CurveDataType.LINEAR) {
                        const startSp = surveyPointsMap.get(segment.startPoint.id);
                        if (startSp) {
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: startSp.pointNumber,
                                point_name: startSp.pointName,
                            });
                        }
                    }
                    if (crossSectionPoints) {
                        const startIndex = crossSectionPoints.findIndex((point) => point.id === segment.startPoint.id);
                        const endIndex = crossSectionPoints.findIndex((point) => point.id === segment.endPoint.id);
                        if (startIndex !== -1 && endIndex !== -1) {
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.CURVE_BEGIN,
                            });
                            for (let i = startIndex + 1; i < endIndex; i++) {
                                const ptSp = surveyPointsMap.get(crossSectionPoints[i].id);
                                if (ptSp) {
                                    centralLineRouteElements.push({
                                        classifier: SIMAElementClassifier.ROUTE_COORD,
                                        point_number: ptSp.pointNumber,
                                        point_name: ptSp.pointName,
                                    });
                                }
                            }
                        }
                    }
                    const endSp = surveyPointsMap.get(segment.endPoint.id);
                    if (endSp) {
                        centralLineRouteElements.push({
                            classifier: SIMAElementClassifier.ROUTE_COORD,
                            point_number: endSp.pointNumber,
                            point_name: endSp.pointName,
                        });
                    }
                }
                break;
            case CurveDataType.CIRCULAR_SEGMENT:
                {
                    if (!segment.previous ||
                        segment.previous.type == CurveDataType.CIRCULAR_SEGMENT) {
                        const startSp = surveyPointsMap.get(segment.startPoint.id);
                        if (startSp) {
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: startSp.pointNumber,
                                point_name: startSp.pointName,
                            });
                        }
                    }
                    const circularSegment = segment;
                    centralLineRouteElements.push({
                        classifier: SIMAElementClassifier.SIMPLE_CURVE,
                        direction: circularSegment.element.isBigArc ? 1 : 0,
                        radius: getSignedRadius(circularSegment),
                    });
                    // C00
                    // list of points within circular segment from centerLine points
                    if (crossSectionPoints) {
                        const startIndex = crossSectionPoints.findIndex((point) => point.id === circularSegment.startPoint.id);
                        const endIndex = crossSectionPoints.findIndex((point) => point.id === circularSegment.endPoint.id);
                        if (startIndex !== -1 && endIndex !== -1) {
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.CURVE_BEGIN,
                            });
                            for (let i = startIndex + 1; i < endIndex; i++) {
                                const ptSp = surveyPointsMap.get(crossSectionPoints[i].id);
                                if (ptSp) {
                                    centralLineRouteElements.push({
                                        classifier: SIMAElementClassifier.ROUTE_COORD,
                                        point_number: ptSp.pointNumber,
                                        point_name: ptSp.pointName,
                                    });
                                }
                            }
                            // C99
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.CURVE_END,
                            });
                        }
                    }
                    if (!segment.next) {
                        const endSp = surveyPointsMap.get(segment.endPoint.id);
                        if (endSp) {
                            centralLineRouteElements.push({
                                classifier: SIMAElementClassifier.ROUTE_COORD,
                                point_number: endSp.pointNumber,
                                point_name: endSp.pointName,
                            });
                        }
                    }
                }
                break;
        }
    }
    const centralLineRoute = {
        routeAttributes: [
            centralLineRouteAttribute,
            {
                classifier: SIMAElementClassifier.ROUTE_ATTRIBUTES,
                additional_distance: 0,
            },
        ],
        routeElements: centralLineRouteElements,
    };
    return centralLineRoute;
};
// For Backward compatibility
const getLineRouteForQuadraticBezierAlignment = (alignment, surveyPointsMap, routeNumber) => {
    const routeAttribute = {
        classifier: SIMAElementClassifier.ROUTE_BEGIN,
        route_number: routeNumber,
        group_number: 1,
        route_name: "Main Line",
        route_type: SIMARouteType.MAIN_LINE,
    };
    const routeElements = [];
    for (const point of alignment
        .pointsToCreateCrossSection) {
        routeElements.push({
            classifier: SIMAElementClassifier.ROUTE_COORD,
            point_number: surveyPointsMap.get(point.id)?.pointNumber,
            point_name: surveyPointsMap.get(point.id)?.pointName,
        });
    }
    const route = {
        routeAttributes: [
            routeAttribute,
            {
                classifier: SIMAElementClassifier.ROUTE_ATTRIBUTES,
                additional_distance: 0,
            },
        ],
        routeElements: routeElements,
    };
    return route;
};
export const alignmentToSIMA = (alignment) => {
    const surveyPointsMap = new Map();
    const pointsForSurveyPoints = alignment
        .pointsToCreateCrossSection;
    if (pointsForSurveyPoints) {
        pointsForSurveyPoints.forEach((surveyPoint, i) => {
            surveyPointsMap.set(surveyPoint.id, {
                pointNumber: i + 1,
                pointName: surveyPoint.label,
                point: surveyPoint.point,
            });
        });
    }
    if (alignment.surveyPoints) {
        alignment.surveyPoints.forEach((surveyPoint) => {
            if (!surveyPointsMap.has(surveyPoint.id)) {
                surveyPointsMap.set(surveyPoint.id, {
                    pointNumber: surveyPointsMap.size + 1,
                    pointName: surveyPoint.name,
                    point: surveyPoint.localPoint,
                });
            }
        });
    }
    if (alignment.ipLinePoints) {
        alignment.ipLinePoints.forEach((ipPoint) => {
            if (!surveyPointsMap.has(ipPoint.id)) {
                surveyPointsMap.set(ipPoint.id, {
                    pointNumber: surveyPointsMap.size + 1,
                    pointName: ipPoint.label,
                    point: ipPoint.localPoint,
                });
            }
        });
    }
    const ipRoute = getIPRoute(alignment.ipLinePoints, surveyPointsMap);
    let mainLineRoute;
    let centralLineRoute;
    if (alignment.horizontalAlignment.type ===
        HorizontalAlignmentType.QUADRATIC_BEZIER) {
        mainLineRoute = getLineRouteForQuadraticBezierAlignment(alignment, surveyPointsMap, 2);
        centralLineRoute = getLineRouteForQuadraticBezierAlignment(alignment, surveyPointsMap, 3);
    }
    else {
        mainLineRoute = getMainLineRoute(alignment, surveyPointsMap);
        centralLineRoute = getCentralLineRoute(alignment, surveyPointsMap);
    }
    const surveyPoints = [];
    surveyPointsMap.forEach((point, i) => {
        surveyPoints.push({
            classifier: SIMAElementClassifier.SURVEY_POINT_COORD,
            point_number: point.pointNumber,
            point_name: point.pointName,
            x: point.point.x,
            y: point.point.y,
            z: point.point.z,
        });
    });
    const simaFormat = {
        comment: {
            classifier: SIMAElementClassifier.COMMENT,
        },
        fileAttributeData: {
            classifier: SIMAElementClassifier.FILE_ATTRIBUTE,
            version: 2,
            site_name: alignment.name,
        },
        surveyPoints,
        simaRoutes: [ipRoute, mainLineRoute, centralLineRoute],
    };
    const simaDoc = SIMADoc.fromFormat(simaFormat);
    return SIMA.fromSIMADoc(simaDoc);
};
//# sourceMappingURL=alignmentToSIMA.js.map